import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const MiniAppsContainer = styled.section`
  padding: 120px 5% 100px;
  background: #000000;
  
  @media (max-width: 768px) {
    padding: 80px 20px 80px;
  }
`;

const MiniAppsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 60px;
  text-align: left;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 400;
  color: white;
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const SectionSubtitle = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  max-width: 600px;
  line-height: 1.6;
`;

const AppsGrid = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 20px;
  scroll-behavior: smooth;
  
  /* Hide scrollbar but keep functionality */
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  min-width: 100px;
`;

const AppIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: ${props => props.color};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.isSelected ? 'rgba(255, 255, 255, 0.3)' : 'transparent'};
  
  &:active {
    transform: scale(0.95);
  }
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 72px;
    height: 72px;
  }
`;

const AppLabel = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: white;
  text-align: center;
  line-height: 1.2;
  max-width: 80px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 11px;
    max-width: 72px;
  }
`;

const ExpansionContainer = styled(motion.div)`
  margin-top: 40px;
  overflow: hidden;
`;

const ExpansionHeader = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 0 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
`;

const ExpansionIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: ${props => props.color};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const ExpansionTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const ExpansionContent = styled(motion.div)`
  padding: 0;
`;

const ExpansionDescription = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  
  &:last-child {
    border-bottom: none;
  }
  
  &::before {
    content: '•';
    color: ${props => props.color || '#4285F4'};
    font-weight: bold;
    font-size: 16px;
    line-height: 1;
    margin-top: 1px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  margin-left: auto;
  
  &:hover {
    color: white;
  }
`;

const CTAContainer = styled.div`
  margin-top: 32px;
  
  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;

const CTAText = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const MiniApps = () => {
  const [selectedApp, setSelectedApp] = useState(null);

  // Apps with detailed descriptions
  const apps = [
    {
      id: 'workflows',
      name: 'Workflows',
      color: '#AAFF00',
      description: 'Automate repetitive tasks and create custom business processes',
      detailedDescription: 'Streamline your music business operations with intelligent automation.',
      features: [
        'Automate repetitive tasks across all your apps',
        'Create custom business processes and workflows',
        'Set up triggers and actions to save time',
        'Integrate with all DIZEE apps seamlessly',
        'Monitor workflow performance and optimize'
      ]
    },
    {
      id: 'bookings',
      name: 'Bookings',
      color: '#FFFF00',
      description: 'Manage show bookings, contracts, and performance schedules',
      detailedDescription: 'All of your live bookings metadata all in one place. Stay on top of pending offers and never miss an opportunity.',
      features: [
        'All of your live bookings metadata all in one place',
        'Stay on top of pending offers',
        'Track ticket counts and venue capacity',
        'To-do lists to make sure you don\'t double book',
        'Hold and confirm shows with ease',
        'Never miss out on any opportunities'
      ]
    },
    {
      id: 'catalog',
      name: 'Catalog',
      color: '#17A2B8',
      description: 'Organize and manage your music catalog, releases, and metadata',
      detailedDescription: 'Manage your entire catalogue with writer credits and releases organized in one place.',
      features: [
        'Entire catalogue organized in one place',
        'Writer credits and releases management',
        'View past and manage upcoming releases',
        'Confirm splits and royalty distribution',
        'Auto-generates label copy for releases',
        'Track release performance and metrics'
      ]
    },
    {
      id: 'tourmngr',
      name: 'TourMngr',
      color: '#FF9500',
      description: 'Plan tours, manage logistics, and coordinate travel arrangements',
      detailedDescription: 'Comprehensive tour management with flight and hotel booking, team coordination, and logistics.',
      features: [
        'Search flights and hotels for tour dates',
        'Send flights and hotel options for approval',
        'Push confirmed shows and travel to touring team',
        'Manage guest lists and crew in one place',
        'Handle routing and logistics coordination',
        'Real-time updates for entire touring team'
      ]
    },
    {
      id: 'notes',
      name: 'Notes',
      color: '#F5F5F5',
      description: 'Capture ideas, meeting notes, and important information',
      detailedDescription: 'Drop in any notes, documents, etc. and let DIZEE organize and use AI to recall notes instantly.',
      features: [
        'Drop in any notes and documents',
        'AI-powered organization and categorization',
        'Recall notes with the click of a button',
        'Smart search across all your notes',
        'Link notes to projects and contacts',
        'Voice-to-text note taking capabilities'
      ]
    },
    {
      id: 'connectors',
      name: 'Connectors',
      color: '#00CED1',
      description: 'Integrate with calendars, streaming platforms, and third-party tools',
      detailedDescription: 'Your way to interact with external apps all through the DIZEE system.',
      features: [
        'Google Calendar integration with AI event creation',
        'AI-powered inbox search from DIZEE',
        'Input info into DIZEE directly from your email',
        'Daily updated data from Spotify for Artists',
        'Bandsintown tour date synchronization',
        'PRO (ASCAP/BMI) royalty data integration',
        'Your way to interact with external apps through DIZEE'
      ]
    },
    {
      id: 'analytics',
      name: 'Analytics',
      color: '#34C759',
      description: 'Track performance metrics, revenue, and audience insights',
      detailedDescription: 'Comprehensive analytics dashboard for all your music business metrics.',
      features: [
        'Track streaming performance across platforms',
        'Revenue analytics and financial insights',
        'Audience demographics and behavior',
        'Tour and show performance metrics',
        'Social media engagement tracking',
        'Predictive analytics for future planning'
      ]
    },
    {
      id: 'knowledge',
      name: 'Knowledge',
      color: '#FF1493',
      description: 'Store and access important documents, contracts, and resources',
      detailedDescription: 'Centralized knowledge base for all your important business documents and resources.',
      features: [
        'Store contracts and legal documents',
        'Organize industry contacts and resources',
        'Quick access to important information',
        'Version control for document updates',
        'Secure document sharing capabilities',
        'AI-powered document search and retrieval'
      ]
    },
    {
      id: 'contacts',
      name: 'Contacts',
      color: '#B08A4C',
      description: 'Manage industry contacts, venues, promoters, and collaborators',
      detailedDescription: 'Comprehensive contact management for all your industry relationships.',
      features: [
        'Manage industry contacts and relationships',
        'Track venues, promoters, and collaborators',
        'Contact history and interaction tracking',
        'Integration with booking and tour management',
        'Smart contact suggestions and recommendations',
        'Automated contact updates and sync'
      ]
    },
    {
      id: 'tasks',
      name: 'Tasks',
      color: '#00CED1',
      description: 'Manage tasks, approvals, and reminders across your music business',
      detailedDescription: 'Centralized task management system with approval workflows and quick reminders.',
      features: [
        'Create and manage tasks and reminders',
        'Review and approve booking requests',
        'Catalog release approval workflow',
        'Tour manager travel and logistics approvals',
        'Team collaboration and task assignment',
        'Automated approval routing and notifications'
      ]
    },
    /* Temporarily hidden - not ready for MVP
    { 
      id: 'merch', 
      name: 'Merch', 
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      icon: '👕'
    },
    { 
      id: 'charting', 
      name: 'Charting', 
      gradient: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
      icon: '📈'
    },
    { 
      id: 'playlisting', 
      name: 'Playlisting', 
      gradient: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)',
      icon: '🎵'
    },
    */
    /* Email app temporarily hidden - not ready for MVP
    { 
      id: 'email', 
      name: 'Email', 
      gradient: 'linear-gradient(135deg, #FA8BFF 0%, #2BD2FF 90%)',
      icon: '✉️'
    }
    */
  ];

  const handleAppClick = (app) => {
    if (selectedApp && selectedApp.id === app.id) {
      setSelectedApp(null); // Close if clicking the same app
    } else {
      setSelectedApp(app); // Open the clicked app
    }
  };

  const closeExpansion = () => {
    setSelectedApp(null);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const appVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  return (
    <MiniAppsContainer>
      <MiniAppsContent>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Apps
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Powerful tools designed specifically for music professionals. From booking management to catalog organization, each app integrates seamlessly to streamline your workflow.
          </SectionSubtitle>
        </SectionHeader>

        <motion.div
          style={{ width: '100%' }}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <AppsGrid>
            {apps.map((app) => (
              <motion.div key={app.id} variants={appVariants}>
                <AppWrapper>
                  <AppIcon
                    color={app.color}
                    isSelected={selectedApp && selectedApp.id === app.id}
                    onClick={() => handleAppClick(app)}
                  />
                  <AppLabel>{app.name}</AppLabel>
                </AppWrapper>
              </motion.div>
            ))}
          </AppsGrid>
        </motion.div>
        
        {/* App Details Expansion */}
        <AnimatePresence>
          {selectedApp && (
            <ExpansionContainer
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ExpansionHeader
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
              >
                <ExpansionIcon color={selectedApp.color} />
                <ExpansionTitle>{selectedApp.name}</ExpansionTitle>
                <CloseButton onClick={closeExpansion}>×</CloseButton>
              </ExpansionHeader>

              <ExpansionContent
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
              >
                <ExpansionDescription>
                  {selectedApp.detailedDescription}
                </ExpansionDescription>

                <FeatureList>
                  {selectedApp.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.4, 
                        delay: 0.3 + (index * 0.1), 
                        ease: 'easeOut' 
                      }}
                    >
                      <FeatureItem color={selectedApp.color}>
                        {feature}
                      </FeatureItem>
                    </motion.div>
                  ))}
                </FeatureList>
              </ExpansionContent>
            </ExpansionContainer>
          )}
        </AnimatePresence>

        <CTAContainer>
          <CTAText>
            Click any app to see more details
          </CTAText>
        </CTAContainer>
      </MiniAppsContent>
    </MiniAppsContainer>
  );
};

export default MiniApps; 